import React from 'react';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';
import BasicForm from '../basicForm/basicForm';
import style from './reviewForm.module.scss';
import ButtonContainer from '../buttonContainer/buttonContainer';
import CheckBox from '../../Form/Preference/CheckBox/CheckBox';
import colors from '../../../scss/colors';

const EditSquareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.4 2.6a2.1 2.1 0 1 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

const FileIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
  </svg>
);

function ReviewSection({ title, onEdit, editLabel, children }) {
  return (
    <section className={style.card}>
      <div className={style.cardHeader}>
        <h4 className={style.cardTitle}>{title}</h4>
        <button type="button" className={style.editIcon} onClick={onEdit} aria-label={editLabel}>
          <EditSquareIcon />
        </button>
      </div>
      <div className={style.cardBody}>{children}</div>
    </section>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className={style.detailRow}>
      <span className={style.key}>{label}</span>
      <span className={style.value}>{value}</span>
    </div>
  );
}

function ReviewForm(props) {
  const { nextHandle, cancel, data, back, navigate, step, totalStep, questionAvailable, jobTitle, setConsentChecked } =
    props;
  const { t } = useTranslation();
  const isJobBeingApplied = useSelector((state) => state.jobs?.pendingApplyV2);

  const attachments = [data?.Cv, data?.CoverLetter, data?.Document1, data?.Document2, data?.Document3].filter(Boolean);

  const answeredQuestions = [
    { question: data?.Question1, answer: data?.Answer1 },
    { question: data?.Question2, answer: data?.Answer2 },
    { question: data?.Question3, answer: data?.Answer3 }
  ].filter((item) => item.question);

  return (
    <BasicForm
      cancel={cancel}
      title={`${t('jobs.quickapply.userdetailheader')} ${jobTitle}`}
      progress="80"
      subtitle={t('jobs.quickapply.reviewform')}
      step={step}
      totalStep={totalStep}
    >
      <div className={style.reviewWrapper}>
        <ReviewSection
          title={t('jobs.quickapply.contactinfo')}
          onEdit={() => navigate('contact')}
          editLabel={t('jobs.quickapply.editcontactinfo')}
        >
          <DetailRow label={`${t('jobs.quickapply.name')}:`} value={`${data?.firstName} ${data?.lastName}`} />
          <DetailRow label={`${t('jobs.quickapply.email')}:`} value={data?.email} />
          <DetailRow
            label={`${t('jobs.quickapply.phone')}:`}
            value={`${data?.PhoneCountryCode} ${data?.Phone}`}
          />
        </ReviewSection>

        <ReviewSection
          title={t('jobs.quickapply.yourattachment')}
          onEdit={() => navigate('attachment')}
          editLabel={t('jobs.quickapply.editattachments')}
        >
          {attachments.map((file) => (
            <div className={style.fileRow} key={file.name}>
              <FileIcon />
              <span className={style.fileName}>{file.name}</span>
            </div>
          ))}
        </ReviewSection>

        {!questionAvailable && answeredQuestions.length > 0 && (
          <ReviewSection
            title={t('jobs.quickapply.youranswers')}
            onEdit={() => navigate('answers')}
            editLabel={t('jobs.quickapply.editanswers')}
          >
            {answeredQuestions.map((item) => (
              <DetailRow key={item.question} label={item.question} value={item.answer ? 'Yes' : 'No'} />
            ))}
          </ReviewSection>
        )}
      </div>

      <p className={style.termsAndConditions}>
        {t('jobs.quickapply.submitconsentprefix')}
        <a href="https://www.airswift.com/terms-conditions" rel="noreferrer" target="_blank">
          {t('jobs.quickapply.termsandconditions')}
        </a>
        {t('jobs.quickapply.and')}
        <a href="https://www.airswift.com/privacy-cookie-policy/" rel="noreferrer" target="_blank">
          {t('jobs.quickapply.privacypolicy')}
        </a>
        {t('jobs.quickapply.submitsessionnotice')}
      </p>
      <div className={style.consentText}>
        <CheckBox
          selected={data?.IsManageConcent}
          className={style.consentText}
          label={t('jobs.quickapply.consent')}
          onChange={(e) => setConsentChecked(e.target.checked)}
        />
      </div>
      <ButtonContainer
        actionCall={nextHandle}
        primaryText={t('common.general.back')}
        disabledPrimary={isJobBeingApplied}
        secondaryText={
          isJobBeingApplied ? (
            <CircularProgress size="1rem" sx={{ color: colors.WHITE }} />
          ) : (
            t('jobs.quickapply.submitapplication')
          )
        }
        back={back}
      />
    </BasicForm>
  );
}

export default ReviewForm;
